import { Box } from "@mui/material";
import { Dayjs } from "dayjs";
import { TimelineZoomProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/TimelineZoomContext";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { EditEventTimelines, PeriodBoundaryMarker } from "./EditEventTimelines";
import { OriginalEventWrapper } from "../hooks/useVideoTimelineData";
import { LabellerDetails } from "src/store/api/generatedApi";
import { TimeAxisLayout } from "./timeAxisLayout";

const EDIT_TIMELINE_MIN_VISIBLE_SECONDS = 5;

interface EditEventTimelineSectionProps {
  workingEvent: ScrapEyesEvent;
  ghostEvents?: ScrapEyesEvent[];
  onGhostEventClick?: (eventId: string) => void;
  clickableGhostEventIds?: ReadonlySet<string>;
  highlightColorsByEventId?: ReadonlyMap<string, string | null>;
  markMergedEventSelected?: boolean;
  overbarHeight?: number;
  beforePeriodBoundary?: PeriodBoundaryMarker;
  afterPeriodBoundary?: PeriodBoundaryMarker;
  minTimingBound?: Dayjs;
  maxTimingBound?: Dayjs;
  alignedTimeAxis?: TimeAxisLayout | null;
  originalEventsByLabeller: {
    labellerDetails: LabellerDetails;
    events: OriginalEventWrapper[];
  }[];
  videoStart: Dayjs;
  videoDuration: number;
  currentTime: number;
  onSeek: (time: number) => void;
  onTimingChange: (updates: { start?: Dayjs; end?: Dayjs }) => void;
  onPickTiming: (event: ScrapEyesEvent) => void;
  onPickArea: (event: ScrapEyesEvent) => void;
  onPickMaterial: (event: ScrapEyesEvent) => void;
  readOnly?: boolean;
  enableZoom?: boolean;
}

export const EditEventTimelineSection = ({
  workingEvent,
  ghostEvents,
  onGhostEventClick,
  clickableGhostEventIds,
  highlightColorsByEventId,
  markMergedEventSelected,
  overbarHeight,
  beforePeriodBoundary,
  afterPeriodBoundary,
  minTimingBound,
  maxTimingBound,
  alignedTimeAxis,
  originalEventsByLabeller,
  videoStart,
  videoDuration,
  currentTime,
  onSeek,
  onTimingChange,
  onPickTiming,
  onPickArea,
  onPickMaterial,
  readOnly = false,
  enableZoom = false,
}: EditEventTimelineSectionProps) => {
  const timelines = (
    <EditEventTimelines
      mergedEvents={[workingEvent]}
      ghostEvents={ghostEvents}
      onGhostEventClick={onGhostEventClick}
      clickableGhostEventIds={clickableGhostEventIds}
      highlightColorsByEventId={highlightColorsByEventId}
      markMergedEventSelected={markMergedEventSelected}
      overbarHeight={overbarHeight}
      beforePeriodBoundary={beforePeriodBoundary}
      afterPeriodBoundary={afterPeriodBoundary}
      minTimingBound={minTimingBound}
      maxTimingBound={maxTimingBound}
      alignedTimeAxis={alignedTimeAxis}
      originalEventsByLabeller={originalEventsByLabeller}
      videoStart={videoStart}
      videoDuration={videoDuration}
      currentTime={currentTime}
      onSeek={onSeek}
      onTimingChange={readOnly ? undefined : onTimingChange}
      onPickTiming={readOnly ? undefined : onPickTiming}
      onPickArea={readOnly ? undefined : onPickArea}
      onPickMaterial={readOnly ? undefined : onPickMaterial}
      enableZoom={enableZoom}
    />
  );

  return (
    <Box
      sx={{
        flex: 1,
        p: 2,
        overflow: "auto",
      }}
    >
      {enableZoom ? (
        <TimelineZoomProvider
          // Remounts (resetting zoom) whenever the edit window itself changes.
          key={`${videoStart.valueOf()}-${videoDuration}`}
          fullDuration={videoDuration}
          videoStart={videoStart}
          minVisibleDurationSeconds={EDIT_TIMELINE_MIN_VISIBLE_SECONDS}
        >
          {timelines}
        </TimelineZoomProvider>
      ) : (
        timelines
      )}
    </Box>
  );
};
